import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export interface ElapsedTimerDisplayProps {
  /**
   * Accepts a Date instance.
   */
  startTime: Date;

  /** Maximum elapsed seconds before the timer stops and unmounts itself. */
  maxDurationSec?: number;

  /**
   * Callback invoked once when elapsedTimeSec >= maxDurationSec.
   * Won’t be called again after cleanup.
   */
  onTimeout?: () => void;

  /**
   * Optional render-prop for custom timer UI.
   * Receives the current elapsed seconds and should return a React node.
   */
  renderElapsedTime?: (elapsedTimeSec: number) => React.ReactNode;
}

export const ElapsedTimerDisplay: React.FC<ElapsedTimerDisplayProps> = ({
  startTime,
  maxDurationSec,
  onTimeout,
  renderElapsedTime
}) => {
  const { t } = useTranslation();

  // Normalize startTime to a millisecond timestamp, and keep it stable across renders
  const startTimeMsRef = useRef<number>(startTime.getTime());

  const [elapsedTimeSec, setElapsedTimeSec] = useState<number>(() =>
    Math.floor((Date.now() - startTimeMsRef.current) / 1000)
  );

  const timerIdRef = useRef<number>();

  useEffect(() => {
    startTimeMsRef.current = startTime.getTime();
    setElapsedTimeSec(0);
  }, [startTime]);

  useEffect(() => {
    let isMounted = true;

    const tick = () => {
      if (!isMounted) return;

      const seconds = Math.floor((Date.now() - startTimeMsRef.current) / 1000);

      if (maxDurationSec !== undefined && seconds >= maxDurationSec) {
        setElapsedTimeSec(seconds);
        onTimeout?.();
        return;
      }

      setElapsedTimeSec(seconds);
      timerIdRef.current = window.setTimeout(tick, 1000);
    };

    timerIdRef.current = window.setTimeout(tick, 1000);

    return () => {
      isMounted = false;
      if (timerIdRef.current !== undefined) {
        clearTimeout(timerIdRef.current);
      }
    };
  }, [maxDurationSec, onTimeout]);

  if (maxDurationSec !== undefined && elapsedTimeSec >= maxDurationSec) {
    return null;
  }

  return renderElapsedTime ? (
    renderElapsedTime(elapsedTimeSec)
  ) : (
    <span role="timer" aria-label="elapsed timer">
      {elapsedTimeSec} {t("seconds").toLowerCase()}
    </span>
  );
};
